import React, { useEffect, useState } from 'react';
import Loader from '../loader/loader';
import { useUpdateProjectMutation } from '../../store/slices/apiSlice/project-api-slice';
import { toastSuccess } from '../../utilities/toast';
import { handleBackendError } from '../../utilities/errorHandling';
import {
    deepObjectCompare,
    getObjectDifferences,
} from '../../utilities/generalHelpers';
import {
    NotesCard,
    ProductConfigurationCard,
    ProductVersionCard,
    ProjectStatusCard,
    RosterCard,
    SiteConfigurationCard,
    UtilitiesCard,
} from './cards';
import ProjectSaveButton from './projectSaveButton';
import ProjectSelector, { ProjectSelectorStatus } from './projectSelector';
import useManageProjectInfo from '../../hooks/useManageProjectInfo';
import {
    broadcastPropertyUpdate,
    PropertyBroadcastMessageTypes,
} from '../../services/broadcastChannels';
import './scoutProject.css';

const projectCards = [
    {
        columnStyle: {},
        columnCards: [
            { Component: ProductVersionCard, name: 'productVersionCard' },
            { Component: ProjectStatusCard, name: 'projectStatusCard' },
            { Component: RosterCard, name: 'rosterCard' },
        ],
    },
    {
        columnStyle: {},
        columnCards: [
            { Component: ProductConfigurationCard, name: 'productConfigurationCard' },
            { Component: SiteConfigurationCard, name: 'siteConfigurationCard' },
            { Component: UtilitiesCard, name: 'utilitiesCard' },
        ],
    },
    {
        columnStyle: { minHeight: '100%' },
        columnCards: [
            { Component: NotesCard, name: 'notesCard', styleClass: 'flex-grow-1' },
        ],
    },
];

const ScoutProject = ({ property }) => {
    const [updateProject, updateProjectResult] = useUpdateProjectMutation();
    const [originalProjectInfo, setOriginalProjectInfo] = useState({});
    const {
        initializeProjectInfo,
        projectInfo,
        updateProjectInfo,
        projectResourceOptions,
    } = useManageProjectInfo();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const { isError, status, isSuccess, data } = updateProjectResult;
        if (isError) {
            handleBackendError('Update Project', updateProjectResult);
        } else if (status === 'fulfilled' && isSuccess) {
            if (data?.result === 'Success') {
                toastSuccess('Project Updated');
                setOriginalProjectInfo(projectInfo);
                // To Do: Optimization is to just broadcast for status type changes
                broadcastPropertyUpdate(
                    PropertyBroadcastMessageTypes.ProjectUpdated,
                    null
                );
            } else {
                handleBackendError('Update Project', updateProjectResult);
            }
        }
    }, [updateProjectResult]);

    const handleUpdate = (updates) => {
        updateProjectInfo(updates);
    };

    const handleSave = () => {
        updateProject({
            projectId: projectInfo.Project.id,
            projectUpdates: getObjectDifferences(originalProjectInfo, projectInfo),
        });
    };

    const isSaveDisabled = deepObjectCompare(originalProjectInfo, projectInfo);

    const handleProjectSelectionStatusChange = (status) => {
        switch (status) {
        case ProjectSelectorStatus.loading:
            setIsLoading(true);
            initializeProjectInfo({});
            break;
        case ProjectSelectorStatus.fulfilled:
            setIsLoading(false);
            break;
        default:
            break;
        }
    };

    const handleProjectSelection = (projectData) => {
        initializeProjectInfo(projectData);
        setOriginalProjectInfo(projectData);
    };

    return (
        <div className="scoutProject">
            <div className="mt-1 d-flex justify-content-between">
                <ProjectSelector
                    projects={property?.Projects}
                    onStatusChange={handleProjectSelectionStatusChange}
                    onProjectSelected={handleProjectSelection}
                />
                <ProjectSaveButton disabled={isSaveDisabled} onSave={handleSave} />
            </div>

            {isLoading || Object.keys(projectInfo)?.length <= 0 ? (
                <Loader />
            ) : (
                <div className="mt-2">
                    <div className="row">
                        {projectCards.map((col, index) => (
                            <div
                                key={`col-${index}`}
                                className="col-md-4 d-flex flex-column"
                                style={col.columnStyle}
                            >
                                {col.columnCards.map((card) => {
                                    const { name, Component, styleClass = '' } = card;
                                    return (
                                        <div
                                            key={name}
                                            className={`projectCardContainer ${styleClass}`}
                                        >
                                            <Component
                                                project={projectInfo}
                                                resourceOptions={projectResourceOptions}
                                                onUpdate={handleUpdate}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScoutProject;
